/** How strictly a numeric grid cell reads what is typed into it. */
export interface NumericInputOptions {
  /**
   * Accept a single leading `-`. Defaults to `true`, which is how the numeric cells have always
   * behaved; pass `false` on columns that can only hold a non-negative figure (rates, volumes).
   */
  allowNegative?: boolean | undefined;
  /**
   * Maximum digits after the decimal point. Omitted, the fraction is uncapped. `0` refuses a
   * decimal point altogether.
   */
  decimals?: number | undefined;
}

/**
 * Reduces raw keystroke text to a well-formed decimal.
 *
 * Everything that is not a digit, a decimal point or an accepted leading minus is dropped — so
 * arithmetic signs (`+`, `*`, `/`, `%`, a trailing `-`) and stray letters never reach the row.
 * A second decimal point is folded into the fraction rather than starting a new one, and the
 * fraction is truncated (never rounded) to `decimals`, so a half-typed value is left alone.
 * @param raw {string} - The input element's current value.
 * @param options {NumericInputOptions} - Sign and precision limits for this column.
 * @returns {string} - The sanitised text to display and commit.
 */
export function sanitizeNumericInput(raw: string, options: NumericInputOptions = {}): string {
  const { allowNegative = true, decimals } = options;
  const text = String(raw ?? '');
  const sign = allowNegative && text.trimStart().startsWith('-') ? '-' : '';
  const digits = text.replace(/[^0-9.]/g, '');
  const [whole = '', ...fractionParts] = digits.split('.');

  if (fractionParts.length === 0 || decimals === 0) return `${sign}${whole}`;

  const fraction = fractionParts.join('');
  const capped = decimals === undefined ? fraction : fraction.slice(0, decimals);
  return `${sign}${whole}.${capped}`;
}

export default sanitizeNumericInput;
